import React, { useEffect, useRef } from "react";

function colormap(v) {
  const stops = [
    [10, 10, 25],
    [10, 60, 130],
    [60, 100, 200],
    [200, 100, 200],
    [255, 180, 60],
    [255, 240, 200],
  ];
  const seg = Math.min(stops.length - 2, Math.floor(v * (stops.length - 1)));
  const t = v * (stops.length - 1) - seg;
  const a = stops[seg];
  const b = stops[seg + 1];
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

export default function Spectrogram({ data, height = 220, progress, onSeek, loading }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(data) || !data.length) return;
    const canvas = canvasRef.current;
    const nMels = data.length;
    const frames = data[0]?.length || 0;
    if (!frames) return;
    canvas.width = frames;
    canvas.height = nMels;
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(frames, nMels);
    for (let y = 0; y < nMels; y++) {
      const freqRow = nMels - 1 - y;
      const row = data[freqRow];
      for (let x = 0; x < frames; x++) {
        const v = row[x] ?? 0;
        const [r, g, b] = colormap(v);
        const idx = (y * frames + x) * 4;
        img.data[idx] = r;
        img.data[idx + 1] = g;
        img.data[idx + 2] = b;
        img.data[idx + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [data]);

  const handleClick = (e) => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p = (e.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(1, p)));
  };

  if (loading || !Array.isArray(data) || !data.length) {
    return (
      <div
        className="w-full rounded-lg bg-white/[0.02] relative overflow-hidden flex items-center justify-center"
        style={{ height }}
        data-testid="spectrogram-loading"
      >
        {/* Subtle shimmer stripes to signal "we're working on it" without
            being noisy. Falls back to a plain spinner if `loading` is
            not set (e.g. genuinely empty data). */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.02) 100%)",
            backgroundSize: "200% 100%",
            animation: loading ? "spec-shimmer 1.1s linear infinite" : "none",
          }}
        />
        <div className="relative flex flex-col items-center gap-2 text-[11px] text-[color:var(--fg-muted)]">
          <svg
            className="animate-spin"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
            <path
              d="M21 12a9 9 0 0 1-9 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span>{loading ? "Rendering spettrogramma…" : "Nessun dato spettrogramma"}</span>
        </div>
      </div>
    );
  }

  const hasProgress = typeof progress === "number" && progress >= 0;

  return (
    <div
      className={`relative rounded-lg overflow-hidden ${onSeek ? "cursor-pointer" : ""}`}
      style={{ height }}
      onClick={handleClick}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ imageRendering: "pixelated", display: "block" }}
      />
      <div className="absolute inset-y-0 left-0 flex flex-col justify-between py-1 px-2 text-[9px] text-white/70 pointer-events-none">
        <span>16k</span>
        <span>4k</span>
        <span>1k</span>
        <span>250</span>
        <span>60Hz</span>
      </div>
      {hasProgress && (
        <div
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: `${progress * 100}%`,
            width: 2,
            background: "rgba(255,255,255,0.9)",
            boxShadow: "0 0 8px rgba(255,255,255,0.6)",
            transform: "translateX(-1px)",
          }}
        />
      )}
    </div>
  );
}
